/**
 * Types for the EP-2 `GET /api/v1/providers/` response (PYMT-2287).
 *
 * The endpoint returns pre-filtered, pre-grouped, pre-sorted and server-localized
 * section cards, so the v1 view renders them as-is — no client-side filtering,
 * grouping, sorting, or eligibility logic. Field names are camelCased from the
 * snake_case backend payload via `snakeToCamel`.
 */

/**
 * Availability states a section can carry, which also drive the notice popup:
 * `enabled` proceeds directly, `blocked`/`confirm` open the popup.
 */
export const V1AvailabilityState = {
  Enabled: "enabled",
  Blocked: "blocked",
  Confirm: "confirm",
} as const;

export type IV1AvailabilityState = (typeof V1AvailabilityState)[keyof typeof V1AvailabilityState];

export interface IV1Notice {
  title: string;
  body: string;
  secondaryAction?: IV1SecondaryAction | null;
}

/**
 * Discriminator for `IV1SecondaryAction.type`.
 *
 * `open_provider` — resolve `providerName` against the sections list and
 * delegate to `handleSelectSection` (used by the Boleto "Try with PIX" CTA).
 * Additional values can be added server-side; the view's dispatcher falls back
 * to a no-op close for unknown types (forward-compat).
 */
export const V1SecondaryActionType = {
  OpenProvider: "open_provider",
  RequestExtension: "request_extension",
} as const;

export type IV1SecondaryActionType = (typeof V1SecondaryActionType)[keyof typeof V1SecondaryActionType];

/**
 * Optional secondary CTA for an availability popup (rendered as the bottom
 * link). Kept generic on purpose — `type` drives the client dispatcher, while
 * `providerName` is only meaningful for `open_provider`.
 */
export interface IV1SecondaryAction {
  type: IV1SecondaryActionType;
  label: string;
  providerName?: string;
}

export interface IV1Availability {
  state: IV1AvailabilityState;
  notice: IV1Notice | null;
  secondaryAction: IV1SecondaryAction | null;
}

/** Fee for a leaf section or drill-in provider: `{value, display, label}`. */
export interface IV1LeafFee {
  value: string;
  display: string | null;
  label: string | null;
}

/** Fee range for a group section: `{min, max, display, label}`. */
export interface IV1GroupFee {
  min: string;
  max: string;
  display: string | null;
  label: string | null;
}

export interface IV1SectionProvider {
  id: string;
  name: string;
  displayName: string;
  type: string;
  requiresPreInstructions: boolean;
}

export interface IV1DrillInProvider {
  id: string;
  name: string;
  displayName: string;
  logoUrl: string;
  type: string;
  fee: IV1LeafFee;
  highlight: boolean;
  availability: IV1Availability;
  requiresPreInstructions: boolean;
}

export interface IV1DrillInSection {
  label: string | null;
  providers: IV1DrillInProvider[];
}

export interface IV1SectionGroup {
  title: string;
  sections: IV1DrillInSection[];
}

/** One section card — a leaf (`provider` set) or a drill-in group (`group` set). */
export interface IV1Section {
  id: string;
  title: string;
  subtitle: string | null;
  logoUrl: string;
  fee: IV1LeafFee | IV1GroupFee;
  highlight: boolean;
  availability: IV1Availability;
  provider: IV1SectionProvider | null;
  group: IV1SectionGroup | null;
}

/** Server-localized, screen-specific strings for the providers screen. */
export interface IV1ProvidersLabels {
  paymentAmount: string;
}

export interface IV1ProvidersResponse {
  title: string;
  notice: IV1Notice | null;
  labels: IV1ProvidersLabels;
  sections: IV1Section[];
}
